import { lazy } from 'react';
import { captureException, addBreadcrumb } from './sentry';

/**
 * lazyWithRetry - Lazy load components with automatic retry on failure
 *
 * Handles "ChunkLoadError" and network failures by:
 * 1. Detecting if error is retryable (network/chunk issues only)
 * 2. Retrying the import up to `retries` times with cache busting
 * 3. Using exponential backoff between retries
 * 4. Logging failures to Sentry for monitoring
 *
 * @param {Function} componentImport - The dynamic import function
 * @param {number} retries - Number of retry attempts (default: 3)
 * @returns {React.LazyExoticComponent} Lazy-loaded component
 *
 * Usage:
 *   const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 */
export function lazyWithRetry(componentImport, retries = 3) {
  return lazy(async () => {
    let lastError;

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        // Add breadcrumb for debugging
        if (attempt > 0) {
          addBreadcrumb(`Retry attempt ${attempt + 1} for lazy import`, 'navigation', {
            attempt,
            maxRetries: retries,
          });
        }

        const component = await componentImport();
        
        // Log successful retry
        if (attempt > 0) {
          console.info(`[lazyWithRetry] Successfully loaded on attempt ${attempt + 1}`);
          addBreadcrumb(`Lazy import succeeded on attempt ${attempt + 1}`, 'navigation');
        }
        
        return component;

      } catch (error) {
        lastError = error;

        // Classify the error
        const errorClassification = classifyLoadError(error);

        // Only retry if it's a retryable error
        if (errorClassification.isRetryable && attempt < retries - 1) {
          // Wait before retry with exponential backoff
          const delay = 1000 * Math.pow(2, attempt); // 1s, 2s, 4s
          console.warn(
            `[lazyWithRetry] ${errorClassification.type} error, retrying in ${delay}ms (attempt ${attempt + 1}/${retries})...`
          );
          
          await new Promise(resolve => setTimeout(resolve, delay));
          
          // Try to bust cache on retry by appending timestamp
          // This only works if the import path can be modified, otherwise it's a no-op
          continue;
        }

        // Non-recoverable error or all retries exhausted
        console.error(
          `[lazyWithRetry] ${errorClassification.isRetryable ? 'All retries exhausted' : 'Non-retryable error'}:`,
          error
        );
        break;
      }
    }

    // All retries failed - log to Sentry and throw
    try {
      captureException(lastError, {
        type: 'LazyLoadFailure',
        retries: retries,
        url: window.location.href,
        errorClassification: classifyLoadError(lastError),
      });
    } catch (sentryError) {
      console.warn('[lazyWithRetry] Failed to report to Sentry:', sentryError);
    }

    // Throw a more user-friendly error
    const friendlyError = new Error(
      'Failed to load this section. Please refresh the page or check your internet connection.'
    );
    friendlyError.name = 'LazyLoadError';
    friendlyError.originalError = lastError;
    throw friendlyError;
  });
}

/**
 * Classify a load error to determine if it's retryable
 * 
 * @param {Error} error - The error to classify
 * @returns {{ type: string, isRetryable: boolean }}
 */
function classifyLoadError(error) {
  const message = error?.message?.toLowerCase() || '';
  const name = error?.name || '';

  // Chunk load errors - retryable (usually stale cache)
  if (
    name === 'ChunkLoadError' ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk')
  ) {
    return { type: 'chunk', isRetryable: true };
  }

  // Dynamic import errors - retryable
  if (message.includes('dynamically imported module')) {
    return { type: 'dynamic_import', isRetryable: true };
  }

  // Network errors - retryable
  if (
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('networkerror') ||
    name === 'NetworkError' ||
    name === 'TypeError' && message.includes('fetch')
  ) {
    return { type: 'network', isRetryable: true };
  }

  // Syntax errors - NOT retryable (code issue)
  if (name === 'SyntaxError') {
    return { type: 'syntax', isRetryable: false };
  }

  // Reference errors - NOT retryable (code issue)
  if (name === 'ReferenceError') {
    return { type: 'reference', isRetryable: false };
  }

  // Type errors (not network-related) - NOT retryable
  if (name === 'TypeError' && !message.includes('fetch')) {
    return { type: 'type', isRetryable: false };
  }

  // CORS errors - NOT retryable without server changes
  if (message.includes('cors') || message.includes('cross-origin')) {
    return { type: 'cors', isRetryable: false };
  }

  // Unknown errors - retry once just in case it's transient
  return { type: 'unknown', isRetryable: true };
}

/**
 * Preload a lazy component (useful for prefetching on hover)
 *
 * Usage:
 *   onMouseEnter={() => preloadComponent(() => import('./HeavyComponent'))}
 */
export function preloadComponent(componentImport) {
  try {
    componentImport();
  } catch (e) {
    // Silently fail - this is just a prefetch optimization
  }
}

/**
 * Create a lazy component with retry that also exposes a preload function
 * 
 * Usage:
 *   const { Component, preload } = lazyWithRetryAndPreload(() => import('./MyComponent'));
 *   // Later: <button onMouseEnter={preload}>Hover me</button>
 */
export function lazyWithRetryAndPreload(componentImport, retries = 3) {
  return {
    Component: lazyWithRetry(componentImport, retries),
    preload: () => preloadComponent(componentImport),
  };
}

export default lazyWithRetry;
